import { MainBody, MainBodyForm } from "@supplier/entity";
import httpRequest from "./index";

import type { Page } from "@supplier/entity";

import { confirmable } from "@/utils/decorator/confirmable";

const PATH = "/main/body";

const service = httpRequest;

/**
 * 分页查询企业主体列表（自定义分页）
 * @param queryParams 查询参数（分页参数/企业名称/主体ID）
 * @returns 分页的企业主体列表数据
 */
export const queryEnterpriseMainBodyPageList = async (queryParams: {
    page?: number;
    size?: number;
    enterpriseName?: string;
    mainBodyId?: number;
}): Promise<Page<MainBody>> => {
    const { data } = await service.get(`${PATH}/custom-paging`, { params: queryParams });
    return data;
};

/**
 * 创建企业主体（带提交确认弹窗）
 * @param formData 企业主体创建表单数据
 * @returns 主体创建接口响应数据
 */
export const createEnterpriseMainBody = confirmable("是否提交")(async (formData: MainBodyForm) => {
    const { data } = await service.post(`${PATH}`, formData);
    return data;
});

/**
 * 修改企业主体信息（带保存确认弹窗）
 * @param mainBodyId 企业主体ID
 * @param formData 企业主体修改表单数据
 */
export const updateEnterpriseMainBody = confirmable("是否提交")(async (mainBodyId: number, formData: MainBodyForm) => {
    await service.put(`${PATH}/${mainBodyId}`, formData, { loading: true });
});

/**
 * 删除企业主体（带删除确认弹窗）
 * @param mainBodyId 企业主体ID（支持数字/字符串类型）
 * @returns 主体删除接口响应数据
 */
export const deleteEnterpriseMainBody = confirmable("是否删除")(async (mainBodyId: number | string) => {
    const { data } = await service.delete(`${PATH}/${mainBodyId}`);
    return data;
});

/**
 * 获取企业主体详情信息
 * @param mainBodyId 企业主体ID（支持数字/字符串类型）
 * @returns 企业主体详情数据
 */
export const getEnterpriseMainBodyDetail = async (mainBodyId: number | string) => {
    const { data } = await service.get(`${PATH}/${mainBodyId}`);
    return data;
};

/**
 * 分页查询已认证的我的企业主体列表（管理员下拉选框专用）
 * @param queryParams 查询参数（分页参数/企业名称/主体ID/租户ID）
 * @returns 分页的企业主体列表数据
 */
export const queryCertifiedMyEnterpriseMainBodyPageList = async (queryParams: {
    page?: number;
    size?: number;
    enterpriseName?: string;
    mainBodyId?: number;
    tenantId?: number;
}): Promise<Page<MainBody>> => {
    const { data } = await service.get(`${PATH}/admin/selector`, { params: queryParams });
    return data;
};

/**
 * 查询当前用户被邀请加入的企业主体列表
 * @returns 企业主体数组
 */
export const getInvitedEnterpriseMainBodyList = async (): Promise<Array<MainBody>> => {
    const { data } = await service.get(`${PATH}/invited/list`);
    return data;
};

/**
 * 根据客户ID查询对应的企业主体信息
 * @param customerId 客户ID
 * @returns 企业主体详情数据
 */
export const getEnterpriseMainBodyByCustomerId = async (customerId: number): Promise<MainBody> => {
    const { data } = await service.get(`${PATH}/find-by-customer/${customerId}`);
    return data;
};

/**
 * 往来客户列表下拉查询企业主体（按企业名称筛选）
 * @param enterpriseName 企业名称（可选）
 * @returns 企业主体数组
 */
export const getEnterpriseMainBodyForDealingsCustomerSelector = async (enterpriseName?: string): Promise<MainBody[]> => {
    const { data } = await service.get(`${PATH}/dealings-customer/selector?enterpriseName=${enterpriseName}`);
    return data;
};

/**
 * 申请加入企业下拉查询企业主体（按企业名称/统一信用代码筛选）
 * @param enterpriseName 企业名称/统一信用代码（可选）
 * @returns 企业主体数组
 */
export const getEnterpriseMainBodyForAddEnterpriseSelector = async (enterpriseName?: string): Promise<MainBody[]> => {
    const { data } = await service.get(`${PATH}/add-enterprise/selector?enterpriseName=${enterpriseName}`);
    return data;
};
